import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import "../../App.css";
import HBLabs from "../../assets/img/HBL_logo.webp";
import {
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemButton,
  Box,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";

import DandDAPI from "@dndUtilities/system_api";
import { RoutePath } from "@utilities/smart_api_class";
import { makeStyles } from "@utilities/theme_utilities";
import LoadingBox from "@components/input/LoadingBox";

export const MENU_ROUTES = {
  user: [
    {
      path: "/dandd/campaign",
      name: "My Campaigns",
    },
    {
      path: "/dandd/character",
      name: "My Characters",
    },
    {
      path: "/dandd/encounter",
      name: "My Encounters",
    },
  ],
  admin: [
    {
      path: "/dandd/all_characters",
      name: "All Characters",
    },
    { 
      path: `/dandd/admin_only`,
      name: "Admin Only"
    },
    {
      path: "/dandd/floating_feature",
      name: "Features",
    },
    {
      path: "/dandd/ticket",
      name: "Tickets",
    },
    {
      path: "/dandd/user_ticket",
      name: "User Tickets",
    },
    {
      path: "/dandd/creature_group",
      name: "Creature Groups",
    },
    {
      path: "/dandd/reusable_string",
      name: "Reusable Strings",
    },
  ],
  sourceBooks: [
    {
      path: "/dandd/source_book",
      name: "Source Books",
    },
  ],
  gameRules: [
    // {
    //   path: "/dandd/adaptation",
    //   name: "Adaptations",
    // },
    {
      path: "/dandd/background",
      name: "Backgrounds",
    },
    {
      path: "/dandd/bastion_facility",
      name: "Bastion Facilities",
    },
    {
      path: "/dandd/game_card",
      name: "Cards",
    },
    {
      path: "/dandd/game_class",
      name: "Classes",
    },
    {
      path: "/dandd/condition",
      name: "Conditions",
    },
    {
      path: "/dandd/creature",
      name: "Creatures",
    },
    {
      path: "/dandd/creature_gift",
      name: "Creature Gifts",
    },
    {
      path: "/dandd/game_deck",
      name: "Decks",
    },
    {
      path: "/dandd/deity",
      name: "Deities",
    },
    {
      path: "/dandd/eldritch_invocation",
      name: "Eldritch Invocations",
    },
    {
      path: "/dandd/equipment_pack",
      name: "Equipment Packs",
    },
    {
      path: "/dandd/feat",
      name: "Feats",
    },
    {
      path: "/dandd/fighting_style",
      name: "Fighting Styles",
    },
    {
      path: "/dandd/gift",
      name: "Gifts",
    },
    {
      path: "/dandd/item",
      name: "Items",
    },
    {
      path: "/dandd/magic_item_keyword",
      name: "Magic Item Keywords",
    },
    {
      path: "/dandd/pact_boon",
      name: "Pact Boons",
    },
    {
      path: "/dandd/race",
      name: "Races/Species",
    },
    {
      path: "/dandd/resource",
      name: "Resources",
    },
    {
      path: "/dandd/rollable_table",
      name: "Rollable Tables",
    },
    {
      path: "/dandd/special_feature",
      name: "Special Features",
    },
    {
      path: "/dandd/spell",
      name: "Spells",
    },
    {
      path: "/dandd/spell_list",
      name: "Spell Lists",
    },
    {
      path: "/dandd/spell_slot_type",
      name: "Spell Slot Tables",
    },
    {
      path: "/dandd/standard_features",
      name: "Standard Features",
    },
    {
      path: "/dandd/subclass",
      name: "Subclasses",
    },
    {
      path: "/dandd/subrace",
      name: "Subraces/Lineages",
    },
    {
      path: "/dandd/table",
      name: "Tables",
    },
    {
      path: "/dandd/tool",
      name: "Tools",
    },
    {
      path: "/dandd/transform",
      name: "Transforms",
    },
    {
      path: "/dandd/weapon_mastery_property",
      name: "Weapon Mastery Properties"
    },
  ],
  info: [
    {
      path: "/attributions",
      name: "Attributions",
    },
    {
      path: "/ogl",
      name: "Open Game License",
    },
    {
      path: "/privacy",
      name: "Privacy Policy",
    },
    {
      path: "/data-deletion",
      name: "Data Deletion",
    },
  ],
};

interface Props {
  logoText: string;
  onNavigate: () => void;
}

const api = DandDAPI.getInstance();

export default function Sidebar({ logoText, onNavigate }: Props) {
  const [reloading, setReloading] = useState(false);
  const [openSections, setOpenSections] = useState<{[key: string]: boolean}>({
    user: true,
    admin: false,
    sourceBooks: false,
    gameRules: false,
    info: false,
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const useStyles = makeStyles()((theme) => {
    return ({
      sidebarList: {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      },
      submenuHeader: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        marginBottom: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          opacity: 0.9,
        },
        '& .MuiListItemText-primary': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      },
      linkButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        marginBottom: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          opacity: 0.9,
        },
        '& .MuiListItemText-primary': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      },
      submenuContainer: {
        border: '1px solid',
        borderTop: 'none',
        marginBottom: 0,
        paddingLeft: theme.spacing(1),
        overflow: 'hidden',
      },
      nestedItem: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        marginBottom: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          opacity: 0.9,
        },
        '& .MuiListItemText-primary': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      }
    });
  });
  const { classes } = useStyles();

  const handleToggle = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const link = (prop: RoutePath, key: number, nested = false) => {
    return (
      <ListItemButton 
        key={key}
        component={NavLink}
        to={prop.path}
        className={nested ? classes.nestedItem : classes.linkButton}
        onClick={onNavigate}
      >
        <ListItemText primary={prop.name} />
      </ListItemButton>
    );
  }

  const submenuHeader = (title: string, section: string) => {
    return (
      <ListItemButton 
        className={classes.submenuHeader}
        onClick={() => handleToggle(section)}
      >
        <ListItemText primary={title} />
        {openSections[section] ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
    );
  };

  const links = (group: keyof typeof MENU_ROUTES, nested = false) => {
    return (
      MENU_ROUTES[group]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((prop, key) => link(prop, key, nested))
    );
  }
  
  const brand = () => {
    return (
      <NavLink to={`/`} className="WhiteTextButton" onClick={onNavigate}>
        <ListItem style={{ height: "55px" }}>
          <img src={HBLabs} alt="logo" className="HBLabs-logo" />
          <ListItemText primary={logoText} />
        </ListItem>
      </NavLink>
    );
  }

  if (reloading) {
    return <LoadingBox />;
  } else {
    return (
      <List className={classes.sidebarList}>
        {brand()}
        
        {/* User Section */}
        {api.user && (
          <>
            {submenuHeader("My Content", "user")}
            <Collapse in={openSections.user} timeout="auto" unmountOnExit>
              <Box className={classes.submenuContainer}>
                <List component="div" disablePadding>
                  {links("user", true)}
                </List>
              </Box>
            </Collapse>
          </>
        )}

        {/* Admin Section */}
        {api.user && api.user.admin && (
          <>
            {submenuHeader("Admin", "admin")}
            <Collapse in={openSections.admin} timeout="auto" unmountOnExit>
              <Box className={classes.submenuContainer}>
                <List component="div" disablePadding>
                  {links("admin", true)}
                </List>
              </Box>
            </Collapse>
          </>
        )}

        {/* Source Books - Direct Links */}
        {links("sourceBooks")}

        {/* Game Rules Section */}
        {submenuHeader("Game Rules", "gameRules")}
        <Collapse in={openSections.gameRules} timeout="auto" unmountOnExit>
          <Box className={classes.submenuContainer}>
            <List component="div" disablePadding>
              {links("gameRules", true)}
            </List>
          </Box>
        </Collapse>

        {/* Info Section */}
        {submenuHeader("Information", "info")}
        <Collapse in={openSections.info} timeout="auto" unmountOnExit>
          <Box className={classes.submenuContainer}>
            <List component="div" disablePadding>
              {links("info", true)}
            </List>
          </Box>
        </Collapse>
      </List>
    );
  }
}